import { useQuery } from '@tanstack/react-query';
import { AgGridReact } from 'ag-grid-react';
import type React from 'react';
import { useMemo, useState } from 'react';
import { ProjectPropertyManageDrawer } from '@/components/ProjectPropertyManageDrawer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import type { ProjectPropertyTableConfig } from '@/lib/projectPropertiesConfig';
import { projectPropertiesService } from '@/services/projectPropertiesService';
import { agGridDefaultColDef, agGridTheme, agGridThemeStyles } from '@/theme/agGridTheme';

interface ProjectPropertyBentoCardProps {
  config: ProjectPropertyTableConfig;
}

export const ProjectPropertyBentoCard: React.FC<ProjectPropertyBentoCardProps> = ({ config }) => {
  const [manageOpen, setManageOpen] = useState(false);

  const {
    data: rows = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: [config.queryKey],
    queryFn: () => projectPropertiesService.getAll(config),
    refetchOnMount: 'always',
  });

  const previewColumns = useMemo(
    () =>
      config.gridColumns.map((col) => ({
        ...col,
        flex: undefined,
        minWidth: col.minWidth ?? 80,
        valueFormatter: (p: { value: unknown }) => (p.value ? String(p.value) : '-'),
      })),
    [config.gridColumns]
  );

  return (
    <>
      <Card className={`flex h-full min-h-[180px] flex-col ${config.bentoClass}`}>
        <CardHeader className="pb-1">
          <CardTitle className="text-sm">{config.title}</CardTitle>
        </CardHeader>
        <CardContent className="flex min-h-0 flex-1 flex-col px-3 pb-2">
          <style>{agGridThemeStyles}</style>
          {isLoading ? (
            <div className="flex flex-1 items-center justify-center">
              <Spinner className="size-6" />
            </div>
          ) : error ? (
            <div className="flex min-h-[120px] flex-1 flex-col items-center justify-center gap-2 text-center text-sm text-danger-600">
              <p>Failed to load</p>
              <Button type="button" variant="outline" size="sm" onClick={() => void refetch()}>
                Retry
              </Button>
            </div>
          ) : (
            <div className="ag-theme-material min-h-[120px] flex-1 w-full overflow-hidden rounded-md border border-border-default">
              <AgGridReact
                theme={agGridTheme}
                rowData={rows}
                columnDefs={previewColumns}
                defaultColDef={{
                  ...agGridDefaultColDef,
                  sortable: true,
                  filter: false,
                  resizable: false,
                }}
                headerHeight={28}
                rowHeight={28}
                suppressCellFocus
                domLayout="normal"
                getRowId={(params) => String(params.data.id)}
              />
            </div>
          )}
        </CardContent>
        <CardFooter className="border-t border-border-default px-3 py-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-full"
            onClick={() => setManageOpen(true)}
          >
            Manage
          </Button>
        </CardFooter>
      </Card>
      <ProjectPropertyManageDrawer config={config} open={manageOpen} onOpenChange={setManageOpen} />
    </>
  );
};
